/*eslint max-lines-per-function: ["error", 200]*/
/*eslint complexity: ["error", 24]*/
import React, { useEffect, useRef } from 'react';
import { Alert, Form } from 'react-bootstrap';
import Select from 'react-select';

import {
  CreateUpdateSalesInvoiceDto,
  GetCompanyDto,
  GetExtendedSalesInvoiceDto,
  GetPurchaserDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import FormRow from '../../../app/components/FormRow';
import {
  SalesInvoiceStatusEnum,
  SalesInvoiceStatusObj,
} from '../../../common/pokCore/validation/enums';
import DynamicTextarea from '../../../app/components/DynamicTextarea';
import momencik from '../../../common/momencik';
import { useTranslation } from '../../../common/hooks/useTranslation';
import DictionarySelector from '../Selects/DictionarySelector';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';

import { SalesInvoiceMissingSymphonyCodeAlert } from './SalesInvoiceMissingSymphonyCodeAlert';
import { AlertNoProjectsInSalesInvoiceItems } from './AlertNoProjectsInSalesInvoiceItems';
import { useSalesInvoiceClientDataChanged } from './useSalesInvoiceClientDataChanged';

interface SalesInvoiceBasicDataEditorProps {
  salesInvoice: CreateUpdateSalesInvoiceDto;
  extendedSalesInvoice?: GetExtendedSalesInvoiceDto;
  propertyChange: (obj: Partial<CreateUpdateSalesInvoiceDto>) => void;
  readOnly?: boolean;
  isOnePositionEnabled: boolean;
  purchaser?: GetPurchaserDto;
  company?: GetCompanyDto;
  refresh?: boolean;
}

const availableStatusesToEditByUser = [
  SalesInvoiceStatusEnum.Draft,
  SalesInvoiceStatusEnum.Generated,
];

const isOnePositionAlertVisible = ({
  isOnePositionEnabled,
  salesInvoice,
}: SalesInvoiceBasicDataEditorProps) =>
  !isOnePositionEnabled && salesInvoice.isOnePositionOnInvoice;

const isInvoiceDateAlertVisible = ({
  salesInvoice,
  readOnly,
}: SalesInvoiceBasicDataEditorProps) =>
  salesInvoice.status === SalesInvoiceStatusEnum.Generated &&
  !readOnly &&
  momencik(salesInvoice.invoiceDate) !== momencik(new Date());

const isMissingSymphonyCodeAlertVisible = ({
  salesInvoice,
}: SalesInvoiceBasicDataEditorProps) =>
  salesInvoice.status !== SalesInvoiceStatusEnum.Sent;

export const SalesInvoiceSummaryEditor: React.FC<
  SalesInvoiceBasicDataEditorProps
> = props => {
  const [showCorrectionReasonText, setShowCorrectionReasonText] =
    React.useState(false);
  const { t, tk } = useTranslation('fvsLng');
  const { salesInvoice, propertyChange, readOnly } = props;
  const onePositionNameRef = useRef<HTMLTextAreaElement>(null);
  const pok = usePokCore();
  const notifications = useNotifications();
  const clientDataChanged = useSalesInvoiceClientDataChanged(
    props.extendedSalesInvoice?.id,
    props.refresh,
  );

  useEffect(() => {
    const dictId = salesInvoice?.correctionReasonDictId;
    if (dictId) {
      pok.dictionaries
        .isOtherCorrectionReason(dictId)
        .then(needReasonTxt => {
          setShowCorrectionReasonText(needReasonTxt.toString() === 'true');
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
        });
    } else {
      setShowCorrectionReasonText(false);
    }
  }, [salesInvoice?.correctionReasonDictId, pok.dictionaries, notifications]);

  const statusOptions = Object.entries(SalesInvoiceStatusEnum).map(
    ([key, value]) => ({
      value: key as SalesInvoiceStatusEnum,
      label: t(SalesInvoiceStatusObj[value]),
    }),
  );

  const correctionReasonChange = async (correctionReasonDictId: string) => {
    propertyChange({ correctionReasonTxt: null, correctionReasonDictId });
  };

  return (
    <>
      <FormRow controlId="Status" label={t(tk.common.status)}>
        <Select
          value={
            salesInvoice.status
              ? statusOptions.find(({ value }) => salesInvoice.status === value)
              : null
          }
          options={statusOptions.filter(({ value }) =>
            availableStatusesToEditByUser.includes(value),
          )}
          onChange={option =>
            propertyChange({
              status: option?.value,
            })
          }
          isDisabled={readOnly}
        />
      </FormRow>
      {salesInvoice.parentId && (
        <>
          <FormRow
            controlId="correctionReasonDict"
            label={t(tk.finance.correctionReason)}
          >
            <DictionarySelector
              dictionary="Przyczyna wystawienia korekty"
              value={salesInvoice?.correctionReasonDictId}
              onChange={option => correctionReasonChange(option.value)}
              readOnly={readOnly}
            />
          </FormRow>
          {showCorrectionReasonText && (
            <FormRow
              controlId="correctionReasonTxt"
              label={t(tk.finance.correctionReasonDesc)}
            >
              <DynamicTextarea
                value={salesInvoice.correctionReasonTxt || ''}
                onChange={e =>
                  propertyChange({ correctionReasonTxt: e.target.value })
                }
                disabled={readOnly}
              />
            </FormRow>
          )}
        </>
      )}
      <FormRow
        controlId="isOnePositionOnInvoice"
        label={t(tk.finance.onePositionOnInvoice)}
      >
        <Form.Check
          type="checkbox"
          className="pt-2"
          checked={salesInvoice.isOnePositionOnInvoice ?? false}
          disabled={readOnly}
          onChange={e => {
            const { checked } = e.target;
            propertyChange({
              isOnePositionOnInvoice: checked,
              onePositionName: checked ? salesInvoice.onePositionName : null!,
            });

            if (checked) {
              setTimeout(() => {
                if (onePositionNameRef.current) {
                  onePositionNameRef.current?.focus();
                }
              }, 1);
            }
          }}
        />
      </FormRow>
      {salesInvoice.isOnePositionOnInvoice && (
        <FormRow controlId="onePositionName" label={t(tk.common.positionName)}>
          <DynamicTextarea
            ref={onePositionNameRef}
            value={salesInvoice.onePositionName || ''}
            onChange={e => propertyChange({ onePositionName: e.target.value })}
            disabled={readOnly}
          />
        </FormRow>
      )}
      <FormRow controlId="textOverItems" label={t(tk.finance.textOverItems)}>
        <DynamicTextarea
          value={salesInvoice.textOverItems || ''}
          onChange={e => propertyChange({ textOverItems: e.target.value })}
          disabled={readOnly}
        />
      </FormRow>
      {isInvoiceDateAlertVisible(props) && (
        <Alert variant="warning">
          <p className="m-0">
            {t(tk.finance.documentApproved)}{' '}
            {momencik(salesInvoice.invoiceDate, 'DD.MM.YYYY')}{' '}
            {t(tk.finance.andSalesDate)}{' '}
            {momencik(salesInvoice.saleDate, 'DD.MM.YYYY')}.
          </p>
        </Alert>
      )}
      {isOnePositionAlertVisible(props) && (
        <Alert variant="warning">
          <p className="m-0">
            {t(tk.finance.diffVatRates)}{' '}
            <b>{t(tk.finance.onePositionOnInvoice)}</b>{' '}
            {t(tk.finance.orSetSameVatRates)}
          </p>
        </Alert>
      )}
      {isMissingSymphonyCodeAlertVisible(props) && (
        <SalesInvoiceMissingSymphonyCodeAlert
          purchaser={props.purchaser}
          recipientClientId={props.extendedSalesInvoice?.recipientClient?.id}
          company={props.company}
        />
      )}
      {(clientDataChanged?.purchaserDataChanged ||
        clientDataChanged?.recipientDataChanged) && (
        <Alert variant="info">
          {clientDataChanged.purchaserDataChanged && (
            <p className="m-0">{t(tk.finance.purchaserDataChangedAlert)}</p>
          )}
          {clientDataChanged.recipientDataChanged && (
            <p className="m-0">{t(tk.finance.recipientDataChangedAlert)}</p>
          )}
          {clientDataChanged.isFormalCorrection && (
            <p className="m-0 mt-1">
              {t(tk.finance.formalCorrectionWillBeIssued)}
            </p>
          )}
        </Alert>
      )}
      <AlertNoProjectsInSalesInvoiceItems
        extendedSalesInvoice={props.extendedSalesInvoice}
      />
    </>
  );
};
